import { useEffect, useState } from "react"
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material"
import { Modal } from "@core/components/modal"
import type {
  RevisionChoices,
  RevisionDifference,
} from "../../hooks/useRevisionConflict"

export interface RevisionConflictDialogProps {
  open?: boolean
  differences: readonly RevisionDifference[]
  getLabel?: (difference: RevisionDifference, index: number) => string
  onApply: (choices: RevisionChoices) => void
  onClose: () => void
}
const labels: Record<string, string> = {
  name: "Название",
  templateVersionId: "Версия шаблона",
  channelId: "Канал",
  schedule: "Расписание",
  enabled: "Включено",
  pin: "Закрепление",
  deletePrevious: "Удаление предыдущего",
}
const displayValue = (value: unknown) =>
  value === undefined ? "Отсутствует" : JSON.stringify(value, null, 2)

/** Applying choices only installs a draft; this dialog has no save action. */
export function RevisionConflictDialog({
  open = true,
  differences,
  getLabel,
  onApply,
  onClose,
}: RevisionConflictDialogProps) {
  const [choices, setChoices] = useState<RevisionChoices>({})
  useEffect(() => setChoices({}), [differences, open])
  const complete = differences.every(
    (difference) =>
      !difference.localChanged ||
      choices[difference.key] === "local" ||
      choices[difference.key] === "server",
  )
  return (
    <Modal
      open={open}
      title="Конфликт изменений"
      onClose={onClose}
      actions={[
        <Button key="cancel" onClick={onClose}>
          Отмена
        </Button>,
        <Button
          key="apply"
          disabled={!complete}
          onClick={() => onApply(choices)}
        >
          Применить выбор
        </Button>,
      ]}
    >
      <Stack spacing={2}>
        <Typography>
          Сравните значения. После применения выбора проверьте черновик и
          сохраните его отдельно.
        </Typography>
        {differences.map((difference, index) => {
          const label =
            getLabel?.(difference, index) ??
            (difference.bindingId === undefined
              ? (labels[difference.field] ?? difference.field)
              : `Подстановка ${index + 1}`)
          return (
            <FormControl key={difference.key} component="fieldset">
              <FormLabel component="legend">{label}</FormLabel>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                {[
                  ["Исходное", difference.baseline],
                  ["Серверное", difference.remote],
                  ["Моё", difference.draft],
                ].map(([title, value]) => (
                  <Box key={String(title)} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption">{String(title)}</Typography>
                    <Box
                      component="pre"
                      sx={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        fontSize: "0.8rem",
                      }}
                    >
                      {displayValue(value)}
                    </Box>
                  </Box>
                ))}
              </Stack>
              {difference.localChanged ? (
                <RadioGroup
                  aria-label={label}
                  value={choices[difference.key] ?? ""}
                  onChange={(_event, value) =>
                    setChoices((previous) => ({
                      ...previous,
                      [difference.key]: value as "server" | "local",
                    }))
                  }
                >
                  <FormControlLabel
                    value="server"
                    control={<Radio />}
                    label="Серверное значение"
                  />
                  <FormControlLabel
                    value="local"
                    control={<Radio />}
                    label="Моё значение"
                  />
                </RadioGroup>
              ) : (
                <Typography variant="caption">
                  Вы не меняли это поле. Будет сохранено серверное значение.
                </Typography>
              )}
            </FormControl>
          )
        })}
      </Stack>
    </Modal>
  )
}
